import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Avatar, AvatarFallback } from "@shadcn-in-nx/ui/avatar";
import { Headphones, User, FileText, Download, Image as ImageIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import Markdown from "react-markdown";
import DOMPurify from "dompurify";
import { ImageGalleryPreview } from "./ImageLightbox";

// Ensure links open safely in a new tab
if (typeof window !== "undefined" && DOMPurify.addHook) {
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A") {
            node.setAttribute("target", "_blank");
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
}

const renderContentHtml = (content) => {
    const hasHtml = /<[a-z][\s\S]*>/i.test(content);
    const html = hasHtml ? content : `<p style="white-space:pre-wrap;margin:0">${content}</p>`;
    return DOMPurify.sanitize(html);
};

const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function SupportFileLink({ file }) {
    return (
        <a href={file.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-white/50 rounded-lg border hover:bg-white/80">
            <FileText className="w-4 h-4 text-gray-500" />
            <span className="text-sm truncate flex-1">{file.name}</span>
            {file.size && <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>}
            <Download className="w-4 h-4 text-gray-500" />
        </a>
    );
}

function SupportFileList({ files }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    if (files.length === 1) return <SupportFileLink file={files[0]} />;

    if (!expanded) {
        return (
            <div className="space-y-2">
                <SupportFileLink file={files[0]} />
                <button className="text-xs text-blue-600 hover:text-blue-800 font-medium px-1" onClick={(e) => { e.stopPropagation(); setExpanded(true); }}>
                    +{files.length - 1} {t("chatMessages.moreFiles")}
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-1">
                {files.map((file, i) => <SupportFileLink key={i} file={file} />)}
            </div>
            <button className="text-xs text-blue-600 hover:text-blue-800 font-medium px-1" onClick={(e) => { e.stopPropagation(); setExpanded(false); }}>
                {t("chatMessages.showLess")}
            </button>
        </div>
    );
}

export default function HumanSupportMessage({ message, onImageClick }) {
    const isStaff = message.role === "human_support_staff";
    const isUser = message.role === "human_support_user";
    const isSystem = message.role === "human_support_system";

    if (isSystem) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-center mb-4"
            >
                <div className={`max-w-md px-4 py-2 rounded-lg text-sm ${
                    message.isError
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-blue-50 text-blue-700 border border-blue-200"
                }`}>
                    <Markdown>{message.content}</Markdown>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex gap-3 mb-4 ${isUser ? "justify-end" : "justify-start"}`}
        >
            <div className={`max-w-[80%] ${
                isStaff
                    ? "bg-blue-50 border border-blue-100"
                    : "bg-gray-100 border border-gray-200"
            } rounded-lg p-4`}>
                <div className="flex items-center gap-2 mb-2">
                    <Avatar className="w-7 h-7">
                        <AvatarFallback className={
                            isStaff
                                ? "bg-blue-200 text-blue-700"
                                : "bg-gray-200 text-gray-600"
                        }>
                            {isStaff ? (
                                <Headphones className="w-4 h-4" />
                            ) : (
                                <User className="w-4 h-4" />
                            )}
                        </AvatarFallback>
                    </Avatar>
                    <div>
                        <p className="text-sm font-medium">
                            {message.senderName || (isStaff ? "Support Team" : "You")}
                        </p>
                        {message.createdAt && (
                            <p className="text-xs text-gray-500">
                                {formatDistanceToNow(new Date(message.createdAt), {
                                    addSuffix: true,
                                })}
                            </p>
                        )}
                    </div>
                </div>

                {message.content && (
                    <div
                        className="text-sm break-words [&_a]:text-blue-600 [&_a]:underline"
                        dangerouslySetInnerHTML={{ __html: renderContentHtml(message.content) }}
                    />
                )}

                {message.files && message.files.length > 0 && (() => {
                    const imageFiles = message.files.filter((f) => f.type?.startsWith("image/") && f.url);
                    const nonImageFiles = message.files.filter((f) => !f.type?.startsWith("image/") || !f.url);
                    return (
                        <div className="mt-3 space-y-2">
                            {imageFiles.length > 0 && (
                                <ImageGalleryPreview
                                    images={imageFiles.map((f) => ({ src: f.url, alt: f.name }))}
                                    onImageClick={onImageClick}
                                />
                            )}
                            {nonImageFiles.length > 0 && (
                                <SupportFileList files={nonImageFiles} />
                            )}
                        </div>
                    );
                })()}
            </div>
        </motion.div>
    );
}
